import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { artworks, galleryFilters, numberWord } from '../content';
import { FadeUp, PageHeader } from '../components/Reveal';
import Lightbox from '../components/Lightbox';

const Gallery = () => {
    const { lang, t } = useLanguage();
    const [activeFilter, setActiveFilter] = useState('all');
    const [openIndex, setOpenIndex] = useState(null);

    const filtered = useMemo(() => {
        if (activeFilter === 'all') return artworks;
        return artworks.filter((a) => Array.isArray(a.cat) && a.cat.includes(activeFilter));
    }, [activeFilter]);

    return (
        <main data-testid="gallery-page">
            <PageHeader
                kicker={`Robin Spencer — ${t.nav.gallery}`}
                title={t.gallery.title}
                sub={`${artworks.length} ${t.gallery.sub}`}
            />

            {/* Filter bar */}
            <section className="px-6 md:px-12 pb-12 max-w-[1400px] mx-auto">
                <FadeUp>
                    <div
                        className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 border-t border-b border-white/10 py-5"
                        data-testid="gallery-filters"
                        role="tablist"
                        aria-label={t.gallery.filterLabel}
                    >
                        <span className="text-[10px] tracking-[0.35em] uppercase text-fog shrink-0">
                            {t.gallery.filterLabel} —
                        </span>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                            {galleryFilters.map((f) => {
                                const active = activeFilter === f.key;
                                return (
                                    <button
                                        key={f.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => {
                                            setActiveFilter(f.key);
                                            setOpenIndex(null);
                                        }}
                                        className={`relative rounded-full border px-4 py-1.5 text-xs tracking-wide transition-colors duration-300 ${
                                            active
                                                ? 'border-dusk bg-dusk/10 text-dusk'
                                                : 'border-white/15 text-fog hover:border-white/40 hover:text-paper'
                                        }`}
                                        data-testid={`gallery-filter-${f.key}`}
                                    >
                                        {f[lang]}
                                    </button>
                                );
                            })}
                        </div>
                        <span className="md:ml-auto text-[10px] tracking-[0.25em] uppercase text-fog/70" data-testid="gallery-count">
                            {filtered.length} / {artworks.length}
                        </span>
                    </div>
                </FadeUp>
            </section>

            <section className="px-6 md:px-12 pb-32 md:pb-44 max-w-[1400px] mx-auto">
                {filtered.length === 0 ? (
                    <p className="text-center text-fog py-24" data-testid="gallery-empty">
                        {t.gallery.empty}
                    </p>
                ) : (
                    <motion.div
                        key={activeFilter}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-8"
                    >
                        {filtered.map((a, i) => (
                            <FadeUp
                                key={a.id}
                                delay={(i % 4) * 0.05}
                                className="mb-8 break-inside-avoid"
                            >
                                <button
                                    onClick={() => setOpenIndex(i)}
                                    className="group block w-full text-left"
                                    data-testid={`gallery-item-${a.id}`}
                                >
                                    <div className="spotlight">
                                        <div className="overflow-hidden">
                                            <img
                                                src={`/images/${a.id}.jpg`}
                                                alt={numberWord(i + 1, lang)}
                                                className="w-full h-auto transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]"
                                                loading="lazy"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-4 flex items-baseline justify-between gap-4 border-b border-white/10 pb-4">
                                        <p className="font-serif italic text-lg md:text-xl tracking-tight text-paper lowercase">{numberWord(i + 1, lang)}</p>
                                        <p className="text-[10px] tracking-[0.25em] uppercase text-fog shrink-0">
                                            {t.gallery.medium}
                                        </p>
                                    </div>
                                </button>
                            </FadeUp>
                        ))}
                    </motion.div>
                )}
            </section>

            <Lightbox
                item={openIndex !== null ? filtered[openIndex].id : null}
                title={openIndex !== null ? numberWord(openIndex + 1, lang) : ''}
                onClose={() => setOpenIndex(null)}
                onPrev={() => setOpenIndex((i) => (i - 1 + filtered.length) % filtered.length)}
                onNext={() => setOpenIndex((i) => (i + 1) % filtered.length)}
            />
        </main>
    );
};

export default Gallery;
